import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export type PromotionKind =
  | 'percentage_off_line'
  | 'fixed_off_line'
  | 'percentage_off_basket'
  | 'fixed_off_basket'
  | 'coupon'
  | 'time_based'
  | 'customer_tier'
  | 'fixed_price'
  | 'group_mix';

export type PromotionScope = 'line' | 'basket' | 'customer' | 'tender';

export type PromotionStatus = 'active' | 'paused' | 'archived';

/** Card-category applicability (YUKI-236); null = for everyone. */
export type CardCategories = { include: string[]; exclude: string[] };

export type PromotionSummary = {
  id: string;
  code: string;
  displayName: string;
  description: string | null;
  kind: PromotionKind;
  scope: PromotionScope;
  priority: number;
  config: Record<string, unknown>;
  couponCode: string | null;
  startsAt: string | null;
  endsAt: string | null;
  exclusiveWithinBucket: boolean;
  stackableWithCustomerDiscount: boolean;
  excludesPromotionCodes: string[];
  /** Absent on a BOS older than YUKI-236; null = for everyone. */
  cardCategories?: CardCategories | null;
  /** EAT-3: skipped by an offline till. Absent on an older BOS = false. */
  onlineOnly?: boolean;
  status: PromotionStatus;
  version: number;
  /**
   * Provenance (YUKI-176); absent on an older BOS = authored in BOS.
   * `connector` (RL-467): imported from a store system.
   */
  source?: 'bos' | 'loyalty' | 'connector';
  externalId?: string | null;
  /** RL-467: the one location it applies at; null / absent = every location. */
  locationId?: string | null;
  importedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

/**
 * Imported from re:form Loyalty (YUKI-176) or from a store system (RL-467):
 * read-only here — BOS refuses edits, archive, pause and activate; the rule is
 * changed where it is mastered.
 */
export function isImportedPromotion(p: Pick<PromotionSummary, 'source'>): boolean {
  return p.source === 'loyalty' || p.source === 'connector';
}

export type PromotionListResponse = {
  items: PromotionSummary[];
  total: number;
  page: number;
  pageSize: number;
};

export type PromotionListQuery = {
  page?: number;
  pageSize?: number;
  status?: PromotionStatus[];
  kind?: PromotionKind[];
  activeNow?: boolean;
};

const LIST_KEY = ['promotions'] as const;
const ONE_KEY = (id: string) => ['promotions', id] as const;

export function usePromotions(query: PromotionListQuery = {}) {
  return useQuery({
    queryKey: [...LIST_KEY, query] as const,
    queryFn: async (): Promise<PromotionListResponse> => {
      const params: Record<string, string | number | boolean> = {};
      if (query.page !== undefined) params.page = query.page;
      if (query.pageSize !== undefined) params.pageSize = query.pageSize;
      if (query.status && query.status.length > 0) {
        params['filter[status]'] = query.status.join(',');
      }
      if (query.kind && query.kind.length > 0) {
        params['filter[kind]'] = query.kind.join(',');
      }
      if (query.activeNow === true) params['filter[activeNow]'] = true;
      const res = await apiClient.get<PromotionListResponse>('/promotions', { params });
      return res.data;
    },
  });
}

export function usePromotion(id: string | undefined) {
  return useQuery({
    queryKey: id === undefined ? ['promotions', '__noop'] : ONE_KEY(id),
    enabled: id !== undefined,
    queryFn: async (): Promise<PromotionSummary> => {
      const res = await apiClient.get<PromotionSummary>(`/promotions/${id}`);
      return res.data;
    },
  });
}

export type PromotionCreateBody = {
  code: string;
  displayName: string;
  description?: string;
  scope: PromotionScope;
  kind: PromotionKind;
  config: Record<string, unknown>;
  priority?: number;
  couponCode?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  exclusiveWithinBucket?: boolean;
  stackableWithCustomerDiscount?: boolean;
  cardCategories?: CardCategories | null;
  onlineOnly?: boolean;
};

export function useCreatePromotion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PromotionCreateBody): Promise<PromotionSummary> => {
      const res = await apiClient.post<PromotionSummary>('/promotions', body);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
    },
  });
}

export type PromotionPatchBody = {
  expectedVersion: number;
  displayName?: string;
  description?: string | null;
  priority?: number;
  couponCode?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  exclusiveWithinBucket?: boolean;
  stackableWithCustomerDiscount?: boolean;
  cardCategories?: CardCategories | null;
  onlineOnly?: boolean;
  config?: Record<string, unknown>;
};

export function useUpdatePromotion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; body: PromotionPatchBody }) => {
      const res = await apiClient.patch<PromotionSummary>(`/promotions/${vars.id}`, vars.body);
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(vars.id) });
    },
  });
}

export function useArchivePromotion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post<PromotionSummary>(`/promotions/${id}/archive`, {});
      return res.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(id) });
    },
  });
}

export function usePausePromotion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post<PromotionSummary>(`/promotions/${id}/pause`, {});
      return res.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(id) });
    },
  });
}

export function useActivatePromotion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post<PromotionSummary>(`/promotions/${id}/activate`, {});
      return res.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(id) });
    },
  });
}

export const KIND_LABEL: Record<PromotionKind, string> = {
  percentage_off_line: '% off line',
  fixed_off_line: 'Fixed off line',
  percentage_off_basket: '% off basket',
  fixed_off_basket: 'Fixed off basket',
  coupon: 'Coupon (gated)',
  time_based: 'Time-based',
  customer_tier: 'Customer tier',
  fixed_price: 'Special price',
  group_mix: 'Group mix (any N: cheapest free or % off the set)',
};

/**
 * Catalogue keys for `KIND_LABEL`. Written out per member rather than composed
 * from the kind at the call site: the parity guard reads source for literal keys.
 */
export const KIND_LABEL_KEY: Record<PromotionKind, string> = {
  percentage_off_line: 'promotions.vocab.kindPercentageOffLine',
  fixed_off_line: 'promotions.vocab.kindFixedOffLine',
  percentage_off_basket: 'promotions.vocab.kindPercentageOffBasket',
  fixed_off_basket: 'promotions.vocab.kindFixedOffBasket',
  coupon: 'promotions.vocab.kindCoupon',
  time_based: 'promotions.vocab.kindTimeBased',
  customer_tier: 'promotions.vocab.kindCustomerTier',
  fixed_price: 'promotions.vocab.kindFixedPrice',
  group_mix: 'promotions.vocab.kindGroupMix',
};

/**
 * Label for a kind, falling back to the raw value for a kind this build does not
 * know. Pass `useT`'s translator to get the operator's language; without it the
 * baked English is returned, which is what the non-React callers still expect.
 */
export function kindLabel(
  kind: string,
  t?: (key: string, bakedEnglish?: string) => string,
): string {
  const english = (KIND_LABEL as Record<string, string | undefined>)[kind];
  if (english === undefined) return kind;
  const key = (KIND_LABEL_KEY as Record<string, string | undefined>)[kind];
  return t !== undefined && key !== undefined ? t(key, english) : english;
}

/** "a, b" from a list; empty string for none. */
export function formatCategoryList(list: readonly string[] | undefined): string {
  return (list ?? []).join(', ');
}

/** Split a comma / newline separated list, trimming and dropping blanks and duplicates. */
export function parseCategoryList(text: string): string[] {
  const out: string[] = [];
  for (const part of text.split(/[,\n]/)) {
    const value = part.trim();
    if (value.length > 0 && !out.includes(value)) out.push(value);
  }
  return out;
}

/** Applicability from the two editor fields; null (for everyone) when both are empty. */
export function cardCategoriesFromText(include: string, exclude: string): CardCategories | null {
  const next = { include: parseCategoryList(include), exclude: parseCategoryList(exclude) };
  return next.include.length === 0 && next.exclude.length === 0 ? null : next;
}

/** The four words `describeCardCategories` assembles its sentence from. */
export type CardCategoryWords = {
  everyone: string;
  only: string;
  not: string;
  cardCategories: string;
};

export const CARD_CATEGORY_WORDS_EN: CardCategoryWords = {
  everyone: 'Everyone',
  only: 'only',
  not: 'not',
  cardCategories: 'Card categories',
};

/** Resolve those four words for the operator's locale. Keys are literal here so
 * the parity guard can see them; the second argument is the baked English. */
export function cardCategoryWords(
  t: (key: string, bakedEnglish?: string) => string,
): CardCategoryWords {
  return {
    everyone: t('promotions.vocab.cardEveryone', 'Everyone'),
    only: t('promotions.vocab.cardOnly', 'only'),
    not: t('promotions.vocab.cardNot', 'not'),
    cardCategories: t('promotions.vocab.cardCategories', 'Card categories'),
  };
}

/**
 * Human summary of applicability for list / detail views. `words` carries the
 * already-translated vocabulary; its default is the English the sentence has
 * always read, so callers and tests that pass nothing are unaffected.
 */
export function describeCardCategories(
  value: CardCategories | null | undefined,
  words: CardCategoryWords = CARD_CATEGORY_WORDS_EN,
): string {
  if (!value || (value.include.length === 0 && value.exclude.length === 0)) return words.everyone;
  const parts: string[] = [];
  if (value.include.length > 0) parts.push(`${words.only} ${formatCategoryList(value.include)}`);
  if (value.exclude.length > 0) parts.push(`${words.not} ${formatCategoryList(value.exclude)}`);
  return `${words.cardCategories}: ${parts.join('; ')}`;
}

export const SCOPE_LABEL: Record<PromotionScope, string> = {
  line: 'Line',
  basket: 'Basket',
  customer: 'Customer',
  tender: 'Tender',
};

export const STATUS_LABEL: Record<PromotionStatus, string> = {
  active: 'Active',
  paused: 'Paused',
  archived: 'Archived',
};

/** Catalogue keys for the two maps above; literals, for the parity guard. */
export const SCOPE_LABEL_KEY: Record<PromotionScope, string> = {
  line: 'promotions.vocab.scopeLine',
  basket: 'promotions.vocab.scopeBasket',
  customer: 'promotions.vocab.scopeCustomer',
  tender: 'promotions.vocab.scopeTender',
};

export const STATUS_LABEL_KEY: Record<PromotionStatus, string> = {
  active: 'promotions.vocab.statusActive',
  paused: 'promotions.vocab.statusPaused',
  archived: 'promotions.vocab.statusArchived',
};

/**
 * Per-kind starting JSON template — pre-filled in the new-promotion
 * form so authors don't have to know the shape from memory.
 */
export const CONFIG_TEMPLATE: Record<PromotionKind, Record<string, unknown>> = {
  percentage_off_line: { percent: 10, filter: { categoryIds: ['cat_id_here'] } },
  fixed_off_line: { amountMinor: '100', filter: { skuList: ['SKU-HERE'] } },
  percentage_off_basket: { percent: 5, minSpendMinor: '5000' },
  fixed_off_basket: { amountMinor: '200', minSpendMinor: '10000' },
  coupon: {
    inner: {
      kind: 'percentage_off_line',
      config: { percent: 10, filter: { categoryIds: ['cat_id_here'] } },
    },
  },
  time_based: {
    inner: {
      kind: 'percentage_off_basket',
      config: { percent: 10 },
    },
    daysOfWeek: [1, 2, 3, 4, 5],
    startTime: '09:00',
    endTime: '17:00',
  },
  customer_tier: { tierCode: 'gold', percent: 15 },
  fixed_price: { specialPriceMinor: '100', filter: { skuList: ['SKU-HERE'] } },
  group_mix: { mixQuantity: 3, freeCheapest: 1, filter: { categoryIds: ['cat_id_here'] } },
};
